/**
 * Security utilities for sanitizing LLM output and validating URLs.
 *
 * This module provides defense-in-depth against XSS attacks from untrusted
 * content (including LLM output, which should be treated as user input).
 *
 * Key security controls:
 * 1. URL validation - blocks javascript:, data:, and other dangerous protocols
 * 2. HTML sanitization - removes scripts, event handlers, and dangerous elements
 * 3. Safe text encoding - escapes HTML entities in plain text
 */

import DOMPurify, { type Config } from "dompurify";

/**
 * Allowed URL protocols for links.
 * Only these protocols are considered safe for href attributes.
 */
const ALLOWED_PROTOCOLS = new Set(["http:", "https:", "mailto:", "tel:"]);

/**
 * DOMPurify configuration for sanitizing HTML content.
 * This is used as an additional layer of defense when rendering markdown.
 */
const DOMPURIFY_CONFIG: Config = {
  // Allow only safe tags
  ALLOWED_TAGS: [
    // Text formatting
    "p",
    "br",
    "span",
    "strong",
    "b",
    "em",
    "i",
    "u",
    "s",
    "strike",
    "del",
    "ins",
    "sub",
    "sup",
    "mark",
    "small",
    "abbr",
    "code",
    "pre",
    "kbd",
    "samp",
    "var",
    // Headings
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    // Lists
    "ul",
    "ol",
    "li",
    "dl",
    "dt",
    "dd",
    // Tables
    "table",
    "thead",
    "tbody",
    "tfoot",
    "tr",
    "th",
    "td",
    "caption",
    "colgroup",
    "col",
    // Block elements
    "div",
    "blockquote",
    "hr",
    "address",
    // Links (href will be validated separately)
    "a",
    // Definition/details
    "details",
    "summary",
    // Other safe elements
    "figure",
    "figcaption",
    "time",
    "cite",
    "q",
  ],
  // Allow only safe attributes
  ALLOWED_ATTR: [
    "href",
    "title",
    "alt",
    "class",
    "id",
    "name",
    "target",
    "rel",
    "colspan",
    "rowspan",
    "scope",
    "headers",
    "datetime",
    "cite",
    "open",
    // Data attributes (for UI purposes, not executable)
    "data-*",
  ],
  // Block dangerous URI schemes
  ALLOWED_URI_REGEXP:
    /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
  // Remove any tags that aren't in the allowed list
  KEEP_CONTENT: true,
  // Don't allow any form of script execution
  FORBID_TAGS: [
    "script",
    "style",
    "iframe",
    "frame",
    "frameset",
    "object",
    "embed",
    "applet",
    "form",
    "input",
    "button",
    "select",
    "textarea",
    "base",
    "meta",
    "link",
    "noscript",
    "template",
    "slot",
    "svg",
    "math",
    "audio",
    "video",
    "source",
    "track",
    "canvas",
    "portal",
  ],
  // Remove all event handlers
  FORBID_ATTR: [
    "onerror",
    "onload",
    "onclick",
    "onmouseover",
    "onmouseout",
    "onmouseenter",
    "onmouseleave",
    "onmousedown",
    "onmouseup",
    "onmousemove",
    "onkeydown",
    "onkeyup",
    "onkeypress",
    "onfocus",
    "onblur",
    "onchange",
    "onsubmit",
    "onreset",
    "onselect",
    "oninput",
    "oncontextmenu",
    "ondblclick",
    "ondrag",
    "ondragend",
    "ondragenter",
    "ondragleave",
    "ondragover",
    "ondragstart",
    "ondrop",
    "onscroll",
    "onwheel",
    "oncopy",
    "oncut",
    "onpaste",
    "onanimationend",
    "onanimationiteration",
    "onanimationstart",
    "ontransitionend",
    "onabort",
    "oncanplay",
    "oncanplaythrough",
    "ondurationchange",
    "onemptied",
    "onended",
    "onloadeddata",
    "onloadedmetadata",
    "onloadstart",
    "onpause",
    "onplay",
    "onplaying",
    "onprogress",
    "onratechange",
    "onseeked",
    "onseeking",
    "onstalled",
    "onsuspend",
    "ontimeupdate",
    "onvolumechange",
    "onwaiting",
    "onshow",
    "ontoggle",
    "onpointerdown",
    "onpointerup",
    "onpointermove",
    "onpointerover",
    "onpointerout",
    "onpointerenter",
    "onpointerleave",
    "onpointercancel",
    "ongotpointercapture",
    "onlostpointercapture",
    "ontouchstart",
    "ontouchmove",
    "ontouchend",
    "ontouchcancel",
    "style", // Prevent style injection (CSS-based attacks)
    "srcset",
    "srcdoc",
    "formaction",
    "xlink:href",
  ],
};

/**
 * Validates a URL to ensure it uses a safe protocol.
 *
 * This function blocks dangerous URL schemes like javascript:, data:, vbscript:, etc.
 * that could be used for XSS attacks.
 *
 * @param url - The URL to validate
 * @returns true if the URL is safe, false otherwise
 */
export function isUrlSafe(url: string | null | undefined): boolean {
  if (!url || typeof url !== "string") {
    return false;
  }

  // Trim whitespace and handle empty strings
  const trimmedUrl = url.trim();
  if (!trimmedUrl) {
    return false;
  }

  try {
    // Handle relative URLs (they're safe since they stay on the same origin)
    if (
      trimmedUrl.startsWith("/") ||
      trimmedUrl.startsWith("./") ||
      trimmedUrl.startsWith("../") ||
      trimmedUrl.startsWith("#")
    ) {
      return true;
    }

    // Parse the URL to extract the protocol
    const parsedUrl = new URL(trimmedUrl, "https://dummy.com");
    const protocol = parsedUrl.protocol.toLowerCase();

    return ALLOWED_PROTOCOLS.has(protocol);
  } catch {
    // If URL parsing fails, be conservative and reject
    // This handles malformed URLs that could be crafted to bypass checks
    return false;
  }
}

/**
 * Sanitizes a URL for use in href attributes.
 *
 * If the URL is safe, returns it unchanged.
 * If the URL is unsafe or malformed, returns "#" as a safe fallback.
 *
 * @param url - The URL to sanitize
 * @returns The sanitized URL or "#" if unsafe
 */
export function sanitizeUrl(url: string | null | undefined): string {
  if (isUrlSafe(url)) {
    return url!.trim();
  }
  return "#";
}

/**
 * Sanitizes HTML content using DOMPurify.
 *
 * This removes scripts, event handlers, and other dangerous elements
 * while preserving safe formatting.
 *
 * Note: This function only works on the client side. On the server,
 * it returns the input unchanged. Use rehype-sanitize for server-side
 * markdown sanitization.
 *
 * @param html - The HTML content to sanitize
 * @returns Sanitized HTML string
 */
export function sanitizeHtml(html: string): string {
  if (!html || typeof html !== "string") {
    return "";
  }
  // DOMPurify requires a DOM environment, only sanitize on client
  if (globalThis.window === undefined) {
    return html;
  }
  return String(DOMPurify.sanitize(html, DOMPURIFY_CONFIG));
}

/**
 * HTML entity encoding map for escaping special characters.
 */
const HTML_ENTITIES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#x27;",
  "/": "&#x2F;",
  "`": "&#x60;",
  "=": "&#x3D;",
};

/**
 * Encodes HTML special characters to prevent XSS.
 *
 * This is the primary control for plain text that shouldn't contain HTML.
 * Use this when displaying user input or LLM output as plain text.
 *
 * @param text - The text to encode
 * @returns HTML-encoded text safe for insertion into the DOM
 */
export function encodeHtmlEntities(text: string): string {
  if (!text || typeof text !== "string") {
    return "";
  }
  return text.replace(/[&<>"'`=/]/g, (char) => HTML_ENTITIES[char] || char);
}

/**
 * Sanitizes text content intended for display.
 *
 * For markdown content, this sanitizes any HTML that might be embedded.
 * For plain text, this encodes HTML entities.
 *
 * @param text - The text to sanitize
 * @param isMarkdown - Whether the text is markdown (vs plain text)
 * @returns Sanitized text
 */
export function sanitizeLlmOutput(
  text: string,
  isMarkdown: boolean = true
): string {
  if (!text || typeof text !== "string") {
    return "";
  }

  if (isMarkdown) {
    // For markdown, we'll let react-markdown handle the rendering,
    // but we sanitize any raw HTML that might be in the content
    // This is defense-in-depth since rehype-raw is already disabled
    return text;
  }

  // For plain text, encode HTML entities to prevent injection
  return encodeHtmlEntities(text);
}

/**
 * Creates a safe href handler for react-markdown link components.
 *
 * This validates the URL and blocks dangerous protocols.
 *
 * @param href - The href value from the markdown
 * @returns A safe href value
 */
export function getSafeHref(href: string | undefined): string {
  return sanitizeUrl(href);
}

/**
 * Hook for DOMPurify to add a target="_blank" to external links
 * and ensure they have rel="noopener noreferrer" for security.
 */
if (globalThis.window !== undefined) {
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    // Set all links to open in new tab with security attributes
    if (node.tagName === "A") {
      const href = node.getAttribute("href") || "";

      // Validate the URL
      if (!isUrlSafe(href)) {
        node.setAttribute("href", "#");
        node.removeAttribute("target");
        return;
      }

      // External links should open in new tab with security attributes
      if (href.startsWith("http://") || href.startsWith("https://")) {
        node.setAttribute("target", "_blank");
        node.setAttribute("rel", "noopener noreferrer");
      }
    }
  });
}

/**
 * Tags emitted by rehype-katex (KaTeX HTML + MathML + the inline SVG it uses
 * for stretchy glyphs like square-root vinculums). These must survive
 * sanitization, otherwise rendered math collapses into garbled, overlapping
 * text. KaTeX output is generated by trusted code from the LaTeX source
 * (KaTeX runs with `trust: false` by default, so it never emits links or
 * scriptable content), and raw HTML is NOT parsed in our markdown pipeline
 * (no rehype-raw), so the model cannot inject these tags itself.
 */
const KATEX_TAG_NAMES = [
  // MathML
  "math",
  "semantics",
  "annotation",
  "annotation-xml",
  "mrow",
  "mi",
  "mo",
  "mn",
  "ms",
  "mtext",
  "mspace",
  "mglyph",
  "mfrac",
  "msqrt",
  "mroot",
  "msub",
  "msup",
  "msubsup",
  "munder",
  "mover",
  "munderover",
  "mmultiscripts",
  "mprescripts",
  "none",
  "mtable",
  "mtr",
  "mtd",
  "mlabeledtr",
  "mpadded",
  "mphantom",
  "mstyle",
  "menclose",
  "merror",
  "maction",
  // Inline SVG used by KaTeX for stretchy/extensible glyphs
  "svg",
  "path",
  "line",
  "g",
  "defs",
  "use",
  "rect",
];

/**
 * Attributes KaTeX places on its output. These are presentational only
 * (no event handlers, no URLs beyond the MathML `xmlns` namespace), so they
 * are safe to allow on any element. `style` is required — KaTeX positions
 * glyphs with inline styles and renders unusable without it.
 */
const KATEX_ATTRIBUTES = [
  "style",
  "ariaHidden",
  "aria-hidden",
  "xmlns",
  "encoding",
  // SVG geometry
  "viewBox",
  "preserveAspectRatio",
  "width",
  "height",
  "d",
  "fill",
  "stroke",
  "strokeWidth",
  "x",
  "y",
  "x1",
  "x2",
  "y1",
  "y2",
  "points",
  // MathML presentational attributes
  "mathvariant",
  "display",
  "displaystyle",
  "scriptlevel",
  "stretchy",
  "fence",
  "accent",
  "accentunder",
  "lspace",
  "rspace",
  "voffset",
  "depth",
  "linethickness",
  "notation",
  "columnalign",
  "rowalign",
  "columnspacing",
  "rowspacing",
  "separator",
];

/**
 * Sanitization schema for rehype-sanitize plugin used with react-markdown.
 * This provides defense-in-depth by sanitizing the HAST (HTML AST) after markdown parsing.
 *
 * Note: Requires `import { defaultSchema } from "rehype-sanitize"` to be used.
 *
 * @param baseSchema - The default schema from rehype-sanitize
 * @returns Extended schema with additional security rules
 */
export const createRehypeSanitizeSchema = <
  T extends { tagNames?: unknown; attributes?: unknown }
>(
  baseSchema: T
) => {
  const tagNames = Array.isArray(baseSchema.tagNames)
    ? baseSchema.tagNames
    : [];
  const attributes =
    baseSchema.attributes && typeof baseSchema.attributes === "object"
      ? baseSchema.attributes
      : {};

  return {
    ...baseSchema,
    // Allow common text formatting and structure elements, plus KaTeX output
    tagNames: [
      ...tagNames,
      "div",
      "span",
      "section",
      "article",
      ...KATEX_TAG_NAMES,
    ],
    // Allow safe attributes only
    attributes: {
      ...attributes,
      "*": ["className", "class", "id", ...KATEX_ATTRIBUTES],
      a: ["href", "title", "target", "rel"],
      // Tables need colspan/rowspan for proper rendering
      td: ["colspan", "rowspan", "align", "valign"],
      th: ["colspan", "rowspan", "align", "valign", "scope"],
    },
    // Block dangerous protocols in URLs
    protocols: {
      href: ["http", "https", "mailto", "tel"],
      src: ["http", "https"],
    },
    // Strip these tags and their contents entirely
    strip: ["script", "style", "iframe", "object", "embed", "form"],
  };
};

const securityUtils = {
  isUrlSafe,
  sanitizeUrl,
  sanitizeHtml,
  encodeHtmlEntities,
  sanitizeLlmOutput,
  getSafeHref,
  createRehypeSanitizeSchema,
};

export default securityUtils;
